/**
 * Banner bajo la Topbar mientras la 2SV todavía no es obligatoria (Vault F2):
 * avisa con la fecha, abre la configuración de Google y re-verifica.
 */
import { ExternalLink, RefreshCw, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GOOGLE_2SV_URL } from './MfaRequiredScreen';

interface MfaEnforcementBannerProps {
  enforceFrom: string | null;
  daysUntil: number | null;
  onRecheck: () => Promise<unknown> | void;
  rechecking?: boolean;
}

export function MfaEnforcementBanner({ enforceFrom, daysUntil, onRecheck, rechecking }: MfaEnforcementBannerProps) {
  const { t, i18n } = useTranslation();
  const date = enforceFrom ? new Date(enforceFrom).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }) : null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-100">
      <ShieldAlert className="h-4 w-4 shrink-0" />
      <span className="font-medium">{t('auth.mfa.banner_title', 'Tu cuenta de Google no tiene verificación en dos pasos.')}</span>
      <span className="text-amber-800/90 dark:text-amber-200/90">
        {date
          ? t('auth.mfa.banner_date', 'Desde el {{date}} va a ser obligatoria para entrar a Internal ({{days}} días).', { date, days: daysUntil ?? 0 })
          : t('auth.mfa.banner_nodate', 'Vas a necesitarla para abrir el Vault y, más adelante, para entrar a Internal.')}
      </span>
      <span className="ml-auto flex items-center gap-2">
        <a
          href={GOOGLE_2SV_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 rounded-md bg-amber-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-800 dark:bg-amber-200 dark:text-amber-950"
        >
          <ExternalLink className="h-3 w-3" />
          {t('auth.mfa.activate', 'Activar en Google')}
        </a>
        <button
          type="button"
          onClick={() => onRecheck()}
          disabled={!!rechecking}
          className="inline-flex items-center gap-1 rounded-md border border-amber-300 px-2.5 py-1 text-xs font-medium hover:bg-amber-100 disabled:opacity-60 dark:border-amber-700 dark:hover:bg-amber-900/40"
        >
          <RefreshCw className={`h-3 w-3 ${rechecking ? 'animate-spin' : ''}`} />
          {rechecking ? t('auth.mfa.rechecking', 'Verificando…') : t('auth.mfa.recheck', 'Ya la activé')}
        </button>
      </span>
    </div>
  );
}
